import { useEffect, useMemo, useRef, useState } from "react";
import BaseDataTableV2 from "../../components/ui/BaseDataTableV2";
import { useQuery, useMutation } from "@tanstack/react-query";
import { Chip, Avatar, Menu, MenuItem } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { fetchCompanies } from "../../services/api/company";
import { debounce } from "../../utils/helpers";
import BaseTextField from "../../components/ui/BaseTextField";
import BaseDropdownV2 from "../../components/ui/BaseDropdownV2";
import dayjs from "dayjs";
import BaseConfirmModal from "../../components/ui/BaseConfirmModal";
import { showToast } from "../../store/toast/toastSlice";
import UserProfileLink from "../../components/ui/UserProfileLink";
import { activateCompanyUser, deactivateCompanyUser } from "../../services/api/company";
import { useDispatch, useSelector } from "react-redux";
import AdminFilters from "./AdminFilters";
import { setAdminFilters } from "../../store/filters/adminFiltersSlice";
import BaseWrapper from "../../components/ui/BaseWrapper";

const menuOptions = [
  {
    label: "View Profile",
    value: "VIEW",
  },
  {
    label: "Approve",
    value: "APPROVE",
    icon: <i className="fa-solid fa-circle-check"></i>,
  },
  {
    label: "Activate",
    value: "APPROVED",
  },
  {
    label: "Deactivate",
    value: "DEACTIVATED",
  },
  {
    label: "Send Email",
    value: "EMAIL",
  },
];

const revenueOptions = {
  1: "Under $1 Million",
  "5-10": "$5 - $10 Million",
  11: "Above $10 Million",
};

const CompanyTable = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [searchQuery, setSearchQuery] = useState("");
  const [isFilterDrawerOpen, setIsFilterDrawerOpen] = useState(false);
  const [companiesList, setCompaniesList] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [rowCount, setRowCount] = useState(0);
  const [isConfirmActivateOpen, setIsConfirmActivateOpen] = useState(false);
  const [isConfirmDeactivateOpen, setIsConfirmDeactivateOpen] = useState(false);
  const [filtersChanged, setFiltersChanged] = useState(false);
  const [contextMenu, setContextMenu] = useState(null);

  const companyFilters = useSelector((state) => state.adminFilters.company);
  const { page, pageSize } = companyFilters.pagination;
  const baseQueryParams = {
    page: page + 1,
    limit: pageSize,
    search: searchQuery,
  };

  const [queryParams, setQueryParams] = useState(baseQueryParams);

  const [paginationModel, setPaginationModel] = useState({
    page,
    pageSize,
  });
  const filtersPayload = useMemo(
    () => ({
      country: companyFilters.country?.label || undefined,
      city: companyFilters.city?.label || undefined,
      status: companyFilters.status || undefined,
      email: companyFilters.email || undefined,
      companyName: companyFilters.companyName || undefined,
      companyType: "FREELANCE",
    }),
    [
      companyFilters.country?.label,
      companyFilters.city?.label,
      companyFilters.status,
      companyFilters.email,
      companyFilters.companyName,
    ],
  );

  const {
    data: companiesData,
    isPending: isCompaniesLoading,
    refetch,
  } = useQuery({
    queryKey: ["companies", queryParams, filtersPayload],
    queryFn: () => fetchCompanies({ ...queryParams, ...filtersPayload }),
    keepPreviousData: true,
  });

  const { mutateAsync: activateUser, isPending: isActivating } = useMutation({
    mutationFn: activateCompanyUser,
    onSuccess: () => {
      dispatch(showToast({ message: "User activated successfully", type: "success" }));
      refetch();
      setIsConfirmActivateOpen(false);
    },
    onError: (error) => {
      dispatch(
        showToast({
          message: error?.response?.data?.message || "Failed to activate user",
          type: "error",
        }),
      );
    },
  });

  const { mutateAsync: deactivateUser, isPending: isDeactivating } = useMutation({
    mutationFn: deactivateCompanyUser,
    onSuccess: () => {
      dispatch(showToast({ message: "User deactivated successfully", type: "success" }));
      refetch();
      setIsConfirmDeactivateOpen(false);
    },
    onError: (error) => {
      dispatch(
        showToast({
          message: error?.response?.data?.message || "Failed to deactivate user",
          type: "error",
        }),
      );
    },
  });

  useEffect(() => {
    setQueryParams((prev) => ({
      ...prev,
      search: searchQuery,
      country: companyFilters.country?.label || "",
      city: companyFilters.city?.label || "",
      status: companyFilters.status || "",
      manager: companyFilters.manager || "",
    }));
  }, [companyFilters, searchQuery]);

  useEffect(() => {
    if (companiesData?.companies) {
      const mapped = companiesData.companies.map((company) => {
        const primaryAddress = company.address || {};
        const isFreelance = company.owner?.role?.role === "FREELANCER";
        return {
          ...company,
          _type: isFreelance ? "freelance" : "client",
          address: primaryAddress.addressLine1 || "-",
          city: primaryAddress.city || "-",
          state: primaryAddress.state || "-",
          zipCode: primaryAddress.zipCode || "-",
          country: primaryAddress.country || "-",
        };
      });

      setCompaniesList(mapped);
      setRowCount(companiesData.totalCount || 0);
    }
  }, [companiesData]);

  const handleContextMenu = (event, item) => {
    event.preventDefault();
    setContextMenu(
      contextMenu === null
        ? {
            mouseX: event.clientX + 2,
            mouseY: event.clientY - 6,
          }
        : null,
    );
    setSelectedCompany(item);
  };

  const handleClose = () => {
    setContextMenu(null);
    setSelectedCompany(null);
  };

  const renderHeaderWithIcon = (title) => (
    <div className="flex items-center gap-2">
      <span className="text-[#525354] font-medium">{title}</span>
    </div>
  );

  const columns = [
    {
      field: "id",
      headerName: "ID",
      width: 80,
      renderHeader: () => renderHeaderWithIcon("ID"),
      renderCell: (params) => (
        <p
          onContextMenu={(e) => handleContextMenu(e, params.row)}
          className="text-primary cursor-pointer"
        >
          {params?.value}
        </p>
      ),
    },
    {
      field: "name",
      headerName: "Full Name",
      width: 180,
      renderHeader: () => renderHeaderWithIcon("Full Name"),
      renderCell: (params) => (
        <div
          onContextMenu={(e) => handleContextMenu(e, params.row)}
          className="flex items-center gap-2 cursor-pointer"
        >
          <Avatar
            src={params.row?.companyLogo?.url || "/default-avatar.png"}
            alt={params.row.name}
            sx={{ width: 32, height: 32 }}
          />
          <p className="text-primary cursor-pointer">{params.row.name}</p>
        </div>
      ),
    },
    {
      field: "freelanceEngineerCount",
      headerName: "Engineers",
      width: 170,
      renderHeader: () => renderHeaderWithIcon("Engineers"),
      renderCell: (params) => (
        <p className="text-primary cursor-pointer items-center">
          {params.row._type === "freelance" ? (params?.value ?? 0) : "-"}
        </p>
      ),
    },
    {
      field: "address",
      headerName: "Address",
      width: 250,
      renderHeader: () => renderHeaderWithIcon("Address"),
    },
    {
      field: "city",
      headerName: "City",
      width: 140,
      renderHeader: () => renderHeaderWithIcon("City"),
    },
    {
      field: "state",
      headerName: "State",
      width: 140,
      renderHeader: () => renderHeaderWithIcon("State"),
    },
    {
      field: "zipCode",
      headerName: "Zip / Post Code",
      width: 130,
      renderHeader: () => renderHeaderWithIcon("Zip / Post Code"),
    },
    {
      field: "country",
      headerName: "Country",
      width: 120,
      renderHeader: () => renderHeaderWithIcon("Country"),
    },
    {
      field: "annualRevenue",
      headerName: "Annual Revenue",
      width: 140,
      renderHeader: () => renderHeaderWithIcon("Annual Revenue"),
      renderCell: (params) => (
        <p className="text-primary cursor-pointer">
          {revenueOptions[params.row.companyAnnualRevenue] || "-"}
        </p>
      ),
    },
    {
      field: "website",
      headerName: "Website",
      width: 150,
      renderHeader: () => renderHeaderWithIcon("Website"),
    },
    {
      field: "createdAt",
      headerName: "Date of Creation",
      width: 180,
      renderHeader: () => renderHeaderWithIcon("Date of Creation"),
      renderCell: (params) => <p>{dayjs(params.value).format("YYYY/MM/DD hh:mm A")}</p>,
    },
    {
      field: "createdBy",
      headerName: "Created By",
      width: 150,
      renderHeader: () => renderHeaderWithIcon("Created By"),
      renderCell: (params) => (
        <UserProfileLink userId={params.row.owner?.id}>
          {params.row.owner?.firstName} {params.row.owner?.lastName}
        </UserProfileLink>
      ),
    },
    {
      field: "projectCount",
      headerName: "Projects",
      width: 100,
      renderHeader: () => renderHeaderWithIcon("Projects"),
      renderCell: (params) => (
        <p className="text-primary cursor-pointer items-center">
          {params.row._type === "freelance" ? "-" : params?.value}
        </p>
      ),
    },
    {
      field: "_count",
      headerName: "Total Work Orders",
      width: 160,
      renderHeader: () => renderHeaderWithIcon("Total Work Orders"),
      renderCell: (params) => (
        <p className="text-primary cursor-pointer items-center">
          {params.row._type === "freelance" ? "-" : params?.value?.workOrders}
        </p>
      ),
    },
    {
      field: "publishedOrRouted",
      headerName: "Published / Routed",
      width: 170,
      renderHeader: () => renderHeaderWithIcon("Published / Routed"),
      renderCell: (params) => (
        <p className="text-primary cursor-pointer">
          {params.row._type === "freelance" ? "-" : params.row.workOrdersByStatus?.PUBLISHED || 0}
        </p>
      ),
    },
    {
      field: "assigned",
      headerName: "Assigned",
      width: 120,
      renderHeader: () => renderHeaderWithIcon("Assigned"),
      renderCell: (params) => (
        <p className="text-primary cursor-pointer">
          {params.row._type === "freelance" ? "-" : params.row.workOrdersByStatus?.ASSIGNED || 0}
        </p>
      ),
    },
    {
      field: "workDone",
      headerName: "Work Done",
      width: 120,
      renderHeader: () => renderHeaderWithIcon("Work Done"),
      renderCell: (params) => (
        <p className="text-primary cursor-pointer">
          {params.row._type === "freelance" ? "-" : params.row.workOrdersByStatus?.WORKDONE || 0}
        </p>
      ),
    },
    {
      field: "approved",
      headerName: "Approved",
      width: 120,
      renderHeader: () => renderHeaderWithIcon("Approved"),
      renderCell: (params) => (
        <p className="text-primary cursor-pointer">
          {params.row._type === "freelance" ? "-" : params.row.workOrdersByStatus?.APPROVED || 0}
        </p>
      ),
    },
    {
      field: "paid",
      headerName: "Paid",
      width: 100,
      renderHeader: () => renderHeaderWithIcon("Paid"),
      renderCell: (params) => (
        <p className="text-primary cursor-pointer">
          {params.row._type === "freelance" ? "-" : params.row.workOrdersByStatus?.PAID || 0}
        </p>
      ),
    },
    {
      field: "status",
      headerName: "Status",
      width: 120,
      renderHeader: () => renderHeaderWithIcon("Status"),
      renderCell: (params) => {
        const s = params?.row?.owner?.status;
        return s === "PENDING" ? (
          <Chip
            label="Pending"
            color="primary"
            size="small"
            variant="outlined"
            className="w-24 justify-center"
          />
        ) : s === "SUBMITTED" ? (
          <Chip label="Submitted" color="primary" size="small" className="w-24 justify-center" />
        ) : s === "APPROVED" ? (
          <Chip label="Active" color="success" size="small" className="w-24 justify-center" />
        ) : s === "UNAPPROVE" ? (
          <Chip label="Un Approve" color="error" size="small" />
        ) : (
          <Chip label="In-Active" color="error" size="small" className="w-24 justify-center" />
        );
      },
    },
    {
      field: "actions",
      headerName: "Actions",
      width: 120,
      renderHeader: () => renderHeaderWithIcon("Actions"),
      renderCell: (params) => {
        const companyStatus = params?.row?.owner?.status;
        const filteredMenuOptions = menuOptions.filter((item) => {
          if (item.value === "APPROVE" && !["PENDING", "SUBMITTED"].includes(companyStatus))
            return false;
          if (
            item.value === "APPROVED" &&
            ["APPROVED", "PENDING", "SUBMITTED"].includes(companyStatus)
          )
            return false;
          if (item.value === "DEACTIVATED" && ["PENDING", "DEACTIVATED"].includes(companyStatus))
            return false;
          return true;
        });
        return (
          <BaseDropdownV2
            menuOptions={filteredMenuOptions}
            triggerElement={<i className="fa-solid fa-ellipsis cursor-pointer ml-4 text-base"></i>}
            onItemClick={(item) => handleItemClick(item, params?.row)}
          />
        );
      },
    },
  ];

  const updateQueryParamsDebounced = useRef(
    debounce((query) => {
      setQueryParams((prev) => ({ ...prev, search: query.trim() || "", page: 1 }));
    }, 500),
  ).current;

  const handleSearchChange = (e) => {
    const query = e.target.value;
    setSearchQuery(query);
    updateQueryParamsDebounced(query);
  };

  const handleCellClick = (params) => {
    if (params.field === "id" || params.field === "name") {
      if (params.row._type === "freelance") {
        navigate(`/user/details/profile/${params?.row?.owner?.id}`);
      } else {
        navigate(`/admin/review/companies/${params?.row?.id}`);
      }
    }
  };

  const handleItemClick = async (item, data) => {
    setSelectedCompany(data);
    if (item === "APPROVED" || item === "ACTIVATE" || item === "APPROVE") {
      setIsConfirmActivateOpen(true);
    } else if (item === "DEACTIVATED") {
      setIsConfirmDeactivateOpen(true);
    } else if (item === "VIEW") {
      navigate(`/user/details/profile/${data.owner?.id}`);
    }
  };

  const handlePaginationChange = (newPaginationModel) => {
    setPaginationModel(newPaginationModel);
    setQueryParams((prev) => ({
      ...prev,
      page: newPaginationModel.page + 1,
      limit: newPaginationModel.pageSize,
    }));
    dispatch(
      setAdminFilters({
        module: "company",
        filterName: "pagination",
        data: { page: newPaginationModel.page, pageSize: newPaginationModel.pageSize },
      }),
    );
  };

  const appliedFilters = useMemo(() => {
    const filters = [];
    if (companyFilters.country?.label)
      filters.push({ prefix: "Country", value: companyFilters.country.label, key: "country" });
    if (companyFilters.city?.label)
      filters.push({ prefix: "City", value: companyFilters.city.label, key: "city" });
    if (companyFilters.manager)
      filters.push({ prefix: "Created By", value: companyFilters.manager, key: "manager" });
    if (companyFilters.companyName)
      filters.push({ prefix: "Vendor", value: companyFilters.companyName, key: "companyName" });
    if (companyFilters.email)
      filters.push({ prefix: "Email", value: companyFilters.email, key: "email" });
    const statusLabelMap = {
      PENDING: "Pending",
      SUBMITTED: "Submitted",
      APPROVED: "Active",
      ACTIVATED: "Active",
      DEACTIVATED: "In-Active",
    };
    if (companyFilters.status) {
      filters.push({
        prefix: "Status",
        value: statusLabelMap[companyFilters.status] || companyFilters.status,
        key: "status",
      });
    }
    return filters;
  }, [companyFilters]);

  const handleRemoveFilter = (key) => {
    if (["companyName", "email", "status", "manager", "companyType"].includes(key)) {
      dispatch(setAdminFilters({ module: "company", filterName: key, data: "" }));
    } else if (key === "city") {
      dispatch(
        setAdminFilters({ module: "company", filterName: key, data: { value: "", label: "" } }),
      );
    } else if (key === "country") {
      dispatch(
        setAdminFilters({ module: "company", filterName: key, data: { value: "", label: "" } }),
      );
      dispatch(
        setAdminFilters({ module: "company", filterName: "city", data: { value: "", label: "" } }),
      );
    }
    dispatch(
      setAdminFilters({
        module: "company",
        filterName: "pagination",
        data: { page: 0, pageSize: companyFilters.pagination.pageSize },
      }),
    );
    setFiltersChanged(true);
  };

  return (
    <>
      <div className="border-b border-zinc-300 bg-white">
        <BaseWrapper>
          <div className="grid grid-cols-7 gap-y-3 py-[7px] items-center">
            <div className="md:col-span-3 col-span-7">
              <h3 className=" md:text-left text-center">Vendors</h3>
            </div>
            <div className="md:col-span-4 col-span-7 flex justify-end">
              {/* <button
                className="bg-primary font-light text-sm text-white px-3 py-1.5 rounded shadow hover:bg-primary/90 transition"
                onClick={() => navigate("/clientSignup")}
              >
                Create New Vendor
              </button> */}
            </div>
          </div>
        </BaseWrapper>
      </div>
      <BaseWrapper>
        <div className="bg-white rounded-lg px-5 py-3.5 shadow-md mt-4 sticky top-10 z-10">
          <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 w-full">
            <div
              className="flex items-center border-r-[1px] border-[#8C8C8C] pr-5 cursor-pointer"
              onClick={() => setIsFilterDrawerOpen(true)}
            >
              <span className="flex items-center justify-center w-7 h-7 rounded-full bg-primary">
                <i className="fa-light fa-filter text-white text-sm"></i>
              </span>
              <p className="text-primary ml-2 ">Add Filter</p>
            </div>
            <div className="flex-1 min-w-0 pl-3 overflow-x-auto whitespace-nowrap py-1 scrollbar">
              <div className="flex gap-2">
                {appliedFilters.map((f) => (
                  <div
                    key={(f.key || "") + (f.value || "") + (f.id || "")}
                    className="flex-shrink-0 flex items-center bg-[#EAF1FF] text-sm font-medium rounded-md px-3 py-1"
                  >
                    <span className="text-primary">{f.prefix}:</span>&nbsp;
                    <p className="text-primary text-sm mx-1">{f.value}</p>
                    <button
                      onClick={() => handleRemoveFilter(f.key, f.id)}
                      className="ml-2 text-primary hover:text-red-500"
                      aria-label={`Remove ${f.prefix} ${f.value}`}
                    >
                      <i className="fas fa-times text-xs"></i>
                    </button>
                  </div>
                ))}
              </div>
            </div>
            <div className="w-full sm:w-[28%]">
              <BaseTextField
                placeholder="Search by name, email or ID"
                value={searchQuery}
                onChange={handleSearchChange}
                startAdornment={<i className="fa-light fa-magnifying-glass text-lg"></i>}
              />
            </div>
          </div>
        </div>

        <BaseDataTableV2
          className="max-h-[calc(100vh-320px)]"
          rowHeight={38}
          columns={columns}
          data={companiesList}
          isLoading={isCompaniesLoading}
          onCellClick={handleCellClick}
          pagination
          paginationMode="server"
          paginationModel={paginationModel}
          onPaginationModelChange={handlePaginationChange}
          rowCount={rowCount}
          pageSizeOptions={[10, 20, 50, 100]}
          hideFooter={false}
          checkboxSelection
        />

        {isFilterDrawerOpen && (
          <AdminFilters
            open={isFilterDrawerOpen}
            onClose={() => setIsFilterDrawerOpen(false)}
            fetchData={refetch}
            isLoading={isCompaniesLoading}
            setFiltersChanged={() => {}}
            entity="company"
          />
        )}

        {isConfirmActivateOpen && (
          <BaseConfirmModal
            open={isConfirmActivateOpen}
            onClose={() => setIsConfirmActivateOpen(false)}
            onConfirm={() => activateUser(String(selectedCompany?.ownerId))}
            isLoading={isActivating}
            title="Are you sure you want to activate this vendor?"
            description="Once activated, the vendor will regain access and its status will be set to Active."
            confirmText="Activate"
            confirmButtonColor="primary"
          />
        )}

        {isConfirmDeactivateOpen && (
          <BaseConfirmModal
            open={isConfirmDeactivateOpen}
            onClose={() => setIsConfirmDeactivateOpen(false)}
            onConfirm={() => deactivateUser(String(selectedCompany?.ownerId))}
            isLoading={isDeactivating}
            title="Are you sure you want to deactivate this vendor?"
            description="Once deactivated, the vendor will lose access and its status will be set to Inactive."
            confirmText="Deactivate"
            confirmButtonColor="error"
          />
        )}

        <Menu
          open={contextMenu !== null}
          onClose={handleClose}
          anchorReference="anchorPosition"
          anchorPosition={
            contextMenu !== null ? { top: contextMenu.mouseY, left: contextMenu.mouseX } : undefined
          }
        >
          <MenuItem
            onClick={() => {
              if (selectedCompany?._type === "freelance") {
                navigate(`/user/details/profile/${selectedCompany?.owner?.id}`);
              } else {
                navigate(`/admin/review/companies/${selectedCompany?.id}`);
              }
              handleClose();
            }}
          >
            <p>Open</p>
          </MenuItem>
          <MenuItem
            onClick={() => {
              if (selectedCompany?._type === "freelance") {
                window.open(`/user/details/profile/${selectedCompany?.owner?.id}`, "_blank");
              } else {
                window.open(`/admin/review/companies/${selectedCompany?.id}`, "_blank");
              }
              handleClose();
            }}
          >
            <p>Open in New Tab</p>
          </MenuItem>
        </Menu>
      </BaseWrapper>
    </>
  );
};

export default CompanyTable;
